/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useState,
} from "react"
import { Row, Switch, Input, Col, Select } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { useLocaleContext } from "common/utils/i18n"
import { CheckableButtonGroup } from "components/common"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { ItemStock, RestockSettingFreq } from "types/models/menu"
import {
  getValidationProps,
  eventValueToCleanNumericInput,
  hasFormErrors,
} from "utils/form_helper"
import {
  DAYS,
  DayType,
  renderRestockLocalTime,
  getNextRestockTimeFormat,
  calculateNextRestockTime,
} from "utils/stock_management_helper"
import { convertDateTZ } from "utils/date_formatter"

import { drawerContentStyle } from "../styles"

const switchStyle = css`
  width: 100%;
  display: flex;
  justify-content: space-between;
`
const formUnsetLineHeight = css`
  .ant-form-item-control {
    line-height: unset;
  }
`
const bold = css`
  font-weight: 500;
`

interface Props extends FormComponentProps {
  itemStock?: ItemStock | null
  timezone?: string
  onChange?: () => void
  isAllowEdit: boolean
}

const StockManagement = forwardRef<FormComponentProps, Props>(
  ({ itemStock, timezone, onChange, isAllowEdit, form }, ref) => {
    const textMap = useTranslationsMapTyped({
      stockManagement: "stocklevel.title",
      setStockLevel: "stocklevel.setStockLevel",
      stockInput: "stocklevel.stockInput",
      stockLevelPlaceholder: "inventory.stock.salesLimitPlaceholder",
      setRestock: "stocklevel.setRestock",
      notMeetMinimumStock: "inventory.stock.notMeetMinimumStock",
      restockStock: "inventory.stock.restockStockInput",
      restockStockPlaceholder: "inventory.stock.restockStockPlaceholder",
      restockFrequency: "inventory.stock.restockFrequency",
      restockLocalTime: "inventory.stock.restockLocalTime",
      restockDays: "inventory.stock.restockDays",
      nextRestock: "inventory.stock.nextRestock",
      nextRestockFormat: "inventory.stock.nextRestockFormat",
      [RestockSettingFreq.Hourly]: "inventory.stock.everyHour",
      [RestockSettingFreq.Daily]: "inventory.stock.everyDay",
      [RestockSettingFreq.Weekly]: "inventory.stock.everyWeek",
      Mon: "common.days.mon",
      Tue: "common.days.tue",
      Wed: "common.days.wed",
      Thu: "common.days.thu",
      Fri: "common.days.fri",
      Sat: "common.days.sat",
      Sun: "common.days.sun",
      pleaseSelectDay: "inventory.stock.pleaseSelectDay",
      noMoreThanSixDays: "stocklevel.hitDayLimitError",
    })

    const { getFieldDecorator, isFieldsTouched, setFieldsValue } = form
    const validationProps = getValidationProps(
      form,
      [
        "currentStock",
        "restockSetting.maxStock",
        "restockSetting.freq",
        "restockSetting.localtime",
        "restockSetting.byDay",
      ],
      true,
    )

    const locale = useLocaleContext("locale")
    const isTouched = isFieldsTouched()
    const isFormError = hasFormErrors(form)

    const [isStockLevelEnabled, setIsStockLevelEnabled] = useState(
      !!itemStock?.enableIms,
    )
    const [isRestockEnabled, setIsRestockEnabled] = useState(
      !!itemStock?.enableRestock,
    )
    const [restockFreq, setRestockFreq] = useState(
      itemStock?.restockSetting?.freq || RestockSettingFreq.Hourly,
    )
    const [restockLocalTime, setRestockLocalTime] = useState(
      itemStock?.restockSetting?.localtime || "00:00",
    )
    const [restockDays, setRestockDays] = useState(
      itemStock?.restockSetting?.byDay || null,
    )
    const [nextRestockTime, setNextRestockTime] = useState<{
      day: string
      hour: string
    } | null>(
      getNextRestockTimeFormat({
        locale,
        nextRestockTime: itemStock?.restockSetting?.nextRestockTime ?? "",
        timezone,
      }),
    )

    useImperativeHandle(ref, () => ({ form }), [form])

    useEffect(() => {
      if (!isRestockEnabled || !isTouched) {
        return
      }

      if (restockFreq === RestockSettingFreq.Weekly && !restockDays?.length) {
        setNextRestockTime(null)
        return
      }

      const nextRestock = calculateNextRestockTime({
        frequency: restockFreq,
        localTime: restockLocalTime,
        days: (restockDays ?? []) as DayType[],
        timezone,
      })
      const nextRestockFormat = getNextRestockTimeFormat({
        locale,
        nextRestockTime: nextRestock,
        timezone,
      })

      setNextRestockTime(nextRestockFormat)
    }, [
      isTouched,
      restockFreq,
      restockLocalTime,
      restockDays,
      locale,
      isRestockEnabled,
      timezone,
    ])
    useEffect(() => {
      if (isTouched && !isFormError) {
        onChange?.()
      }
    }, [isTouched, isFormError, onChange])

    const stockNumberValidator = (rule: any, value: any, callback: any) => {
      const stock = Number(value)
      const min = 1
      const max = 9999

      if (Number.isNaN(stock) || stock < min || stock > max) {
        callback(
          textMap.notMeetMinimumStock
            .replace("<min>", min.toLocaleString())
            .replace("<max>", max.toLocaleString()),
        )
      } else {
        callback()
      }
    }

    const handleRestockEnabled = (enabled: boolean) => {
      setIsRestockEnabled(enabled)

      if (!enabled) {
        return
      }

      const nowHour = convertDateTZ(undefined, timezone).hour()
      const localTime = `${nowHour < 10 ? 0 : ""}${nowHour}:00`

      setRestockFreq(
        itemStock?.restockSetting?.freq || RestockSettingFreq.Hourly,
      )
      setRestockLocalTime(itemStock?.restockSetting?.localtime || localTime)
      setRestockDays(itemStock?.restockSetting?.byDay || null)
    }

    const handleStockLevelEnabled = (enabled: boolean) => {
      setIsStockLevelEnabled(enabled)
      handleRestockEnabled(!!itemStock?.enableRestock)
    }

    const handleChangeRestockFreq = (freq: RestockSettingFreq) => {
      const nowHour = convertDateTZ(undefined, timezone).hour()
      const localTime = `${nowHour < 10 ? 0 : ""}${nowHour}:00`

      setFieldsValue({ restockSetting: { localtime: localTime } })
      setRestockFreq(freq)
      setRestockLocalTime(localTime)
      setRestockDays(null)
    }

    return (
      <Fragment>
        <div className="section-separator" data-testid="stock-header">
          <span>{textMap.stockManagement}</span>
        </div>
        <div css={drawerContentStyle}>
          <Row>
            <Form.Item label={textMap.setStockLevel} css={switchStyle}>
              {getFieldDecorator("enableIms", {
                initialValue: isStockLevelEnabled,
                valuePropName: "checked",
              })(
                <Switch
                  data-testid="enableIms-switch"
                  onChange={handleStockLevelEnabled}
                  disabled={!isAllowEdit}
                />,
              )}
            </Form.Item>
          </Row>
          {isStockLevelEnabled && (
            <Fragment>
              <Row
                css={css`
                  margin-bottom: 18px;
                `}
              >
                <Form.Item
                  label={textMap.stockInput}
                  {...validationProps.currentStock}
                >
                  {getFieldDecorator("currentStock", {
                    initialValue: itemStock?.currentStock,
                    getValueFromEvent: eventValueToCleanNumericInput,
                    rules: [
                      {
                        required: true,
                        validator: stockNumberValidator,
                      },
                    ],
                  })(
                    <Input
                      data-testid="currentStock-input"
                      placeholder={textMap.stockLevelPlaceholder}
                      disabled={!isAllowEdit}
                    />,
                  )}
                </Form.Item>
              </Row>
              <Row>
                <Form.Item label={textMap.setRestock} css={switchStyle}>
                  {getFieldDecorator("enableRestock", {
                    initialValue: isRestockEnabled,
                    valuePropName: "checked",
                  })(
                    <Switch
                      data-testid="enableRestock-switch"
                      onChange={handleRestockEnabled}
                      disabled={!isAllowEdit}
                    />,
                  )}
                </Form.Item>
              </Row>
            </Fragment>
          )}
          {isStockLevelEnabled && isRestockEnabled && (
            <Fragment>
              <Row>
                <Form.Item
                  label={textMap.restockStock}
                  {...validationProps["restockSetting.maxStock"]}
                >
                  {getFieldDecorator("restockSetting.maxStock", {
                    initialValue: itemStock?.restockSetting?.maxStock,
                    getValueFromEvent: eventValueToCleanNumericInput,
                    rules: [
                      {
                        required: true,
                        validator: stockNumberValidator,
                      },
                    ],
                  })(
                    <Input
                      data-testid="restockStock-input"
                      placeholder={textMap.restockStockPlaceholder}
                      disabled={!isAllowEdit}
                    />,
                  )}
                </Form.Item>
              </Row>
              <Row>
                <Col span={11} data-testid="select-restock-freq">
                  <Form.Item
                    label={textMap.restockFrequency}
                    {...validationProps["restockSetting.freq"]}
                  >
                    {getFieldDecorator("restockSetting.freq", {
                      initialValue: restockFreq,
                      rules: [{ required: true }],
                    })(
                      <Select
                        style={{ width: "100%" }}
                        onChange={handleChangeRestockFreq}
                        disabled={!isAllowEdit}
                      >
                        {Object.values(RestockSettingFreq).map(freq => (
                          <Select.Option key={freq} value={freq}>
                            {textMap[freq]}
                          </Select.Option>
                        ))}
                      </Select>,
                    )}
                  </Form.Item>
                </Col>
                <Col span={2} />
                {restockFreq && restockFreq !== RestockSettingFreq.Hourly && (
                  <Col span={11} data-testid="select-restock-time">
                    <Form.Item
                      label={textMap.restockLocalTime}
                      {...validationProps["restockSetting.localtime"]}
                    >
                      {getFieldDecorator("restockSetting.localtime", {
                        initialValue: restockLocalTime,
                        rules: [{ required: true }],
                      })(
                        <Select
                          showSearch
                          style={{ width: "100%" }}
                          onChange={setRestockLocalTime}
                          disabled={!isAllowEdit}
                        >
                          {renderRestockLocalTime()}
                        </Select>,
                      )}
                    </Form.Item>
                  </Col>
                )}
              </Row>
              {restockFreq === RestockSettingFreq.Weekly && (
                <Row data-testid="select-restock-days">
                  <Form.Item
                    label={textMap.restockDays}
                    {...validationProps["restockSetting.byDay"]}
                  >
                    {getFieldDecorator("restockSetting.byDay", {
                      initialValue: restockDays ?? [],
                      rules: [
                        {
                          required: true,
                          message: textMap.pleaseSelectDay,
                        },
                        {
                          validator: (rule, value, callback) => {
                            if (value && value.length > 6) {
                              callback(textMap.noMoreThanSixDays)
                            } else {
                              callback()
                            }
                          },
                        },
                      ],
                    })(
                      <CheckableButtonGroup
                        options={DAYS.map(day => ({
                          label: textMap[day],
                          value: day,
                        }))}
                        onChange={setRestockDays}
                        checkedValues={restockDays ?? []}
                        disabled={!isAllowEdit}
                      />,
                    )}
                  </Form.Item>
                </Row>
              )}
              <Row
                data-testid="next-restock-detail"
                css={css`
                  margin-top: 8px;
                `}
              >
                <Form.Item
                  label={textMap.nextRestock}
                  css={formUnsetLineHeight}
                >
                  {!nextRestockTime ? (
                    <span>-</span>
                  ) : (
                    <Fragment>
                      <span css={bold}>{nextRestockTime.day}</span>
                      <span>
                        {textMap.nextRestockFormat
                          .replace("<daydate.medium>", "")
                          .replace("<HH:MM>", "")}
                      </span>
                      <span css={bold}>{nextRestockTime.hour}</span>
                    </Fragment>
                  )}
                </Form.Item>
              </Row>
            </Fragment>
          )}
        </div>
      </Fragment>
    )
  },
)

export default Form.create<Props>({})(StockManagement)
